import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiGet, apiPost, apiDelete } from '../../lib/api'
import { 
  Award, 
  Plus, 
  Trash2, 
  ChevronRight,
  MapPin,
  Clock,
  ArrowLeft,
  Search,
  X,
  AlertCircle,
  Sparkles,
  Loader2,
  CheckCircle,
  MinusCircle
} from 'lucide-react'

interface UserProfession {
  id: string
  professionId: string
  professionName: string
  professionAbbreviation?: string | null
  state: string
  ceRequirements?: {
    totalHours: number
    renewalPeriodMonths: number
  }
}

interface Profession {
  id: string
  name: string
  abbreviation?: string
}

interface AiLookupResult {
  totalHours: number | null
  renewalPeriodYears: number | null
  categories: CeCategory[]
  specialRequirements: string
  confidence: number
}

interface CeCategory {
  name: string
  requiredHours: number
  isPediatricContent?: boolean
  isOneTime?: boolean
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
]

export default function ProfessionsPage() {
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [professionSearch, setProfessionSearch] = useState('')
  const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null)
  const [selectedState, setSelectedState] = useState('')
  const [error, setError] = useState('')

  const [showRequestModal, setShowRequestModal] = useState(false)
  const [requestStep, setRequestStep] = useState<1 | 2>(1)
  const [newProfessionName, setNewProfessionName] = useState('')
  const [newProfessionState, setNewProfessionState] = useState('')
  const [aiResult, setAiResult] = useState<AiLookupResult | null>(null)
  const [editedTotalHours, setEditedTotalHours] = useState('')
  const [editedRenewalYears, setEditedRenewalYears] = useState('2')
  const [editedSpecialRequirements, setEditedSpecialRequirements] = useState('')
  const [editedCategories, setEditedCategories] = useState<CeCategory[]>([])
  const [requestError, setRequestError] = useState('')

  const { data: userProfessionsData, isLoading } = useQuery({
    queryKey: ['/api/user/professions'],
    queryFn: () => apiGet<{ professions: UserProfession[] }>('/user/professions'),
  })

  const { data: professionsData } = useQuery({
    queryKey: ['/api/professions', professionSearch],
    queryFn: () => apiGet<{ professions: Profession[] }>(`/professions?search=${encodeURIComponent(professionSearch)}`),
    enabled: professionSearch.length > 1,
  })

  const addMutation = useMutation({
    mutationFn: (data: { professionId: string; state: string }) => 
      apiPost('/user/professions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      closeModal()
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to add renewal')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/user/professions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
    },
  })

  const aiLookupMutation = useMutation({
    mutationFn: (data: { professionName: string; state: string }) =>
      apiPost<AiLookupResult>('/professions/ai-lookup', data),
    onSuccess: (data) => {
      setAiResult(data)
      setEditedTotalHours(data.totalHours?.toString() || '')
      setEditedRenewalYears(data.renewalPeriodYears?.toString() || '2')
      setEditedSpecialRequirements(data.specialRequirements || '')
      setEditedCategories(data.categories || [])
      setRequestStep(2)
    },
    onError: () => {
      setAiResult({ totalHours: null, renewalPeriodYears: 2, categories: [], specialRequirements: '', confidence: 0 })
      setEditedTotalHours('')
      setEditedRenewalYears('2')
      setEditedSpecialRequirements('')
      setEditedCategories([])
      setRequestStep(2)
    },
  })

  const submitRequestMutation = useMutation({
    mutationFn: (data: {
      professionName: string
      state: string
      totalHours: number | null
      renewalPeriodYears: number
      ceCategories: CeCategory[]
      specialRequirements: string
      aiConfidence: number
    }) => apiPost('/professions/request', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/professions'] })
      closeRequestModal()
      window.alert('Your renewal has been added successfully!')
    },
    onError: (err) => {
      setRequestError(err instanceof Error ? err.message : 'Failed to submit request')
    },
  })

  const userProfessions = userProfessionsData?.professions || []
  const availableProfessions = professionsData?.professions || []

  const closeModal = () => {
    setShowAddModal(false)
    setProfessionSearch('')
    setSelectedProfession(null)
    setSelectedState('')
    setError('')
  }

  const closeRequestModal = () => {
    setShowRequestModal(false)
    setRequestStep(1)
    setNewProfessionName('')
    setNewProfessionState('')
    setAiResult(null)
    setEditedTotalHours('')
    setEditedRenewalYears('2')
    setEditedSpecialRequirements('')
    setEditedCategories([])
    setRequestError('')
  }

  const handleAdd = () => {
    if (!selectedProfession || !selectedState) {
      setError('Please select both a renewal and state')
      return
    }
    addMutation.mutate({
      professionId: selectedProfession.id,
      state: selectedState,
    })
  }

  const handleOpenRequestModal = () => {
    setShowAddModal(false)
    setNewProfessionName(professionSearch)
    setShowRequestModal(true)
  }

  const handleAiLookup = () => {
    if (!newProfessionName.trim() || !newProfessionState) {
      setRequestError('Please enter a renewal name and select a state')
      return
    }
    setRequestError('')
    aiLookupMutation.mutate({
      professionName: newProfessionName.trim(),
      state: newProfessionState,
    })
  }

  const handleSubmitRequest = () => {
    submitRequestMutation.mutate({
      professionName: newProfessionName.trim(),
      state: newProfessionState,
      totalHours: editedTotalHours ? parseFloat(editedTotalHours) : null,
      renewalPeriodYears: parseInt(editedRenewalYears) || 2,
      ceCategories: editedCategories,
      specialRequirements: editedSpecialRequirements,
      aiConfidence: aiResult?.confidence || 0,
    })
  }

  const showNoResults = professionSearch.length > 1 && availableProfessions.length === 0 && !selectedProfession

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/profile" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Manage Renewals</h1>
          <p className="text-sm text-gray-500">Track CE requirements by renewal and state</p>
        </div>
      </div>

      {/* Add Button */}
      <button
        onClick={() => setShowAddModal(true)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Add Renewal
      </button>

      {/* Renewals List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : userProfessions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Renewals Added</h3>
          <p className="text-gray-500">
            Add a renewal to start tracking CE requirements
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200">
          {userProfessions.map((profession) => (
            <div
              key={profession.id}
              className="flex items-center justify-between p-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    {profession.professionName}{profession.professionAbbreviation ? ` (${profession.professionAbbreviation})` : ''}
                  </h3>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {profession.state}
                    </span>
                    {profession.ceRequirements && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {profession.ceRequirements.totalHours} hrs / {profession.ceRequirements.renewalPeriodMonths / 12} yr
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => deleteMutation.mutate(profession.id)}
                disabled={deleteMutation.isPending}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal (existing profession search) */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add Renewal</h3>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Profession Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renewal
                </label>
                {selectedProfession ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="font-medium text-blue-700">{selectedProfession.name}{selectedProfession.abbreviation ? ` (${selectedProfession.abbreviation})` : ''}</span>
                    <button
                      onClick={() => setSelectedProfession(null)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={professionSearch}
                      onChange={(e) => setProfessionSearch(e.target.value)}
                      placeholder="Search renewals..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {availableProfessions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                        {availableProfessions.map((prof) => (
                          <button
                            key={prof.id}
                            onClick={() => {
                              setSelectedProfession(prof)
                              setProfessionSearch('')
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50"
                          >
                            {prof.name}{prof.abbreviation ? ` (${prof.abbreviation})` : ''}
                          </button>
                        ))}
                      </div>
                    )}
                    {showNoResults && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3">
                        <p className="text-sm text-gray-500 mb-2">No renewals found for "{professionSearch}"</p>
                        <button
                          onClick={handleOpenRequestModal}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 hover:bg-purple-100 transition-colors text-sm font-medium"
                        >
                          <Sparkles className="w-4 h-4" />
                          Request "{professionSearch}" as a new renewal
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* State Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!selectedProfession || !selectedState || addMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {addMutation.isPending ? 'Adding...' : 'Add Renewal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Profession Modal (2-step) */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {requestStep === 1 ? 'Add New Renewal' : 'Confirm CE Requirements'}
              </h3>
              <button onClick={closeRequestModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {requestError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{requestError}</p>
              </div>
            )}

            {requestStep === 1 ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enter the renewal name and state. We'll find the CE requirements for you.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewal Name
                  </label>
                  <input
                    type="text"
                    value={newProfessionName}
                    onChange={(e) => setNewProfessionName(e.target.value)}
                    placeholder="e.g. Registered Nurse, Physical Therapist..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <select
                    value={newProfessionState}
                    onChange={(e) => setNewProfessionState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={closeRequestModal}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAiLookup}
                    disabled={!newProfessionName.trim() || !newProfessionState || aiLookupMutation.isPending}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {aiLookupMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Finding...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Find Requirements
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {aiResult && aiResult.confidence > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    aiResult.confidence >= 70 
                      ? 'bg-green-50 border border-green-200 text-green-700' 
                      : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                  }`}>
                    {aiResult.confidence >= 70 ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    Accuracy: {aiResult.confidence}%
                  </div>
                )}

                <p className="text-sm text-gray-600">
                  Please verify and edit the CE requirements below before saving.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total CE Hours Required
                  </label>
                  <input
                    type="number"
                    value={editedTotalHours}
                    onChange={(e) => setEditedTotalHours(e.target.value)}
                    placeholder="e.g. 40"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewal Period (years)
                  </label>
                  <input
                    type="number"
                    value={editedRenewalYears}
                    onChange={(e) => setEditedRenewalYears(e.target.value)}
                    placeholder="e.g. 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CE Categories
                  </label>
                  <div className="space-y-2">
                    {editedCategories.map((cat, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={cat.name}
                          onChange={(e) => {
                            const updated = [...editedCategories]
                            updated[idx] = { ...updated[idx], name: e.target.value }
                            setEditedCategories(updated)
                          }}
                          placeholder="Category name"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="number"
                          value={cat.requiredHours}
                          onChange={(e) => {
                            const updated = [...editedCategories]
                            updated[idx] = { ...updated[idx], requiredHours: parseFloat(e.target.value) || 0 }
                            setEditedCategories(updated)
                          }}
                          placeholder="Hours"
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => setEditedCategories(editedCategories.filter((_, i) => i !== idx))}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                        >
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setEditedCategories([...editedCategories, { name: '', requiredHours: 0 }])}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add Category
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Requirements / Notes
                  </label>
                  <textarea
                    value={editedSpecialRequirements}
                    onChange={(e) => setEditedSpecialRequirements(e.target.value)}
                    rows={3}
                    placeholder="Any additional notes about CE requirements..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setRequestStep(1)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitRequest}
                    disabled={submitRequestMutation.isPending}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitRequestMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Save & Add Renewal'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
